import { ApiService }                            from '../../app-lite/api/api.service';
import { CarfaxService }                         from '../../app-lite/carfax/carfax.service';
import { CheckaService }                         from '../checka/checka.service';
import { CompanyIntegrationsModel }              from '../../app-lite/companies/models/company.integrations';
import { ConfirmService }                        from '../../app-lite/messages/confirm/confirm.service';
import { DvlaLookupService }                     from '../../app-lite/dvla-lookup/dvla-lookup.service';
import { IntegrationQuotePricesModel }           from '../../app-lite/integrations/typings/integration.quote-prices';
import { PurchaseRequestLogModel }               from '../reports/models/purchase-request.log';
import { SessionService }                        from '../../app-lite/sessions/session.service';
import { VehicleModel }                          from '../../app-lite/vehicles/models/vehicle';
import { VehicleRegistrationCheckaQuoteModel }   from './models/vehicle-registration.checka-quote';
import { VehicleRegistrationRequestQuoteModel }  from './models/vehicle-registration.request-quote';
import { VehicleRegistrationTireModel }          from './models/vehicle-registration.tire';

import PurchaseVehicleRegistrationRequestsHtml from './vehicle-registration.purchase-requests.html';
import VehicleRegistrationTireDetailsHtml      from './vehicle-registration.tire-details.html';

export class VehicleRegistrationServiceAjs {
  $q           : ng.IQService;
  $rootScope   : any;
  $translate   : ng.translate.ITranslateService;
  $uibModal    : ng.ui.bootstrap.IModalService;
  carfaxApi    : CarfaxService;
  checkaApi    : CheckaService;
  confirm      : ConfirmService;
  dvlaLookupApi: DvlaLookupService;
  gridApi      : any;
  messages     : any;
  sessionObjAPI: SessionService;
  workshopAPI  : ApiService;

  constructor (
    $q           : ng.IQService,
    $rootScope   : any,
    $translate   : ng.translate.ITranslateService,
    $uibModal    : ng.ui.bootstrap.IModalService,
    carfaxApi    : CarfaxService,
    checkaApi    : CheckaService,
    confirm      : ConfirmService,
    dvlaLookupApi: DvlaLookupService,
    gridAPI      : any,
    messages     : any,
    sessionObjAPI: SessionService,
    workshopAPI  : ApiService
  ) {
    this.$uibModal     = $uibModal;
    this.$q            = $q;
    this.$rootScope    = $rootScope;
    this.$translate    = $translate;
    this.carfaxApi     = carfaxApi;
    this.checkaApi     = checkaApi;
    this.confirm       = confirm;
    this.dvlaLookupApi = dvlaLookupApi;
    this.gridApi       = gridAPI;
    this.messages      = messages;
    this.sessionObjAPI = sessionObjAPI;
    this.workshopAPI   = workshopAPI;
  }

  activate () : ng.IPromise<boolean> {
    return this.confirm.generic(this.$translate.instant('JS_SPACE.INTEGRATIONS.ACTIVATE', {
      name: this.$translate.instant('COMPANY_SPACE.INTEGRATIONS.VEHICLE_REGISTRATIONS.LOOKUP')
    }))
    .then(() => {
      return this.$rootScope.Company.country('australia')
        ? this.workshopAPI.get('/company_integration/add_registration_lookup')
        : this.workshopAPI.get('/company/add_checka');
    });
  }

  confirmPurchase ( quote : VehicleRegistrationRequestQuoteModel ) : ng.IPromise<boolean> {
    return this.$rootScope.Company.country('australia')
      ? this.workshopAPI.get('/purchase_integration_lookups/purchase/true', [ quote.quantity, 'registration_lookup' ])
      : this.workshopAPI.post('/chargify/purchase', {
      quantity: quote.quantity,
      service : 'checka'
    });
  }

  deactivate () : ng.IPromise<boolean> {
    return this.confirm.generic(this.$translate.instant('JS_SPACE.INTEGRATIONS.DEACTIVATE', {
      name: this.$translate.instant('COMPANY_SPACE.INTEGRATIONS.VEHICLE_REGISTRATIONS.LOOKUP')
    }))
    .then(() => {
      return this.$rootScope.Company.country('australia')
        ? this.workshopAPI.get('/company_integration/remove_registration_lookup')
        : this.workshopAPI.get('/company/remove_checka');
    });
  }

  findVehicle ( vehicle : VehicleModel ) : ng.IPromise<VehicleModel> {
    return this.workshopAPI.post('/vehicles/find_on_registration_lookup', vehicle, 'vehicle');
  }

  getCurrentPrice ( quantity : number, prices: IntegrationQuotePricesModel ) : string {
    if (quantity < 300) {
      return prices.high_price;
    }

    if (quantity < 500) {
      return prices.medium_price;
    }

    if (quantity >= 500) {
      return prices.low_price;
    }

    return '0.00';
  }

  getLookupCount () : ng.IPromise<number> {
    return this.workshopAPI.get('/dashboard/purchased_request_logs_count');
  }

  getLookups ( params : any = {} ) : ng.IPromise<Array<PurchaseRequestLogModel>> {
    return this.workshopAPI.get('/dashboard/purchased_request_logs_index', [
      params.pageSize || 5,
      params.offset || 0
    ]);
  }

  getQuote ( quote : VehicleRegistrationRequestQuoteModel ) : ng.IPromise<VehicleRegistrationRequestQuoteModel> {
    if (!quote.quantity) {
      return this.$q.when(quote);
    }

    return this.$rootScope.Company.country('australia')
    ? this.workshopAPI.get('/purchase_integration_lookups/quote', [ quote.quantity, 'registration_lookup' ])
    .then(( total : string ) => {
      quote.total = total;

      return quote;
    })
    : this.$q.when()
      .then(() => {
        if (!quote.quantity) {
          quote.total = '0.00';

          return quote;
        }

        const price    = new BigDecimal(quote.price ? quote.price.toString() : '0.00');
        const quantity = new BigDecimal(quote.quantity ? quote.quantity.toString() : '0');

        quote.total =  price.multiply(quantity).toString();

        return quote;
      });
  }

  getRequestPrices () : ng.IPromise<IntegrationQuotePricesModel> {
    return this.$rootScope.Company.country('australia')
      ? this.workshopAPI.get('/purchase_integration_lookups/current_lookup_cost/registration_lookup')
      : this.workshopAPI.get('/chargify/service_cost', 'checka')
      .then(( result : VehicleRegistrationCheckaQuoteModel ) => {
        return {
          high_price  : result.price.toString(),
          low_price   : result.price.toString(),
          medium_price: result.price.toString()
        };
      });
  }

  getRequests () : ng.IPromise<number> {
    return this.$rootScope.Company.country('australia')
      ? this.workshopAPI.get('/company_integrations')
      .then(( settings : CompanyIntegrationsModel ) => settings.registration_lookup_count)
      : this.$q.when(this.$rootScope.Company.company.checka_requests);
  }

  getTireDetails ( vehicle : VehicleModel ) : ng.IPromise<VehicleRegistrationTireModel> {
    return this.workshopAPI.get('/vehicles/get_tyre_details', vehicle.registration_lookup_vehicle_id)
    .then(( details : VehicleRegistrationTireModel ) => {
      details.Front.Tyres.FitmentType = details.FitmentType;
      details.Front.Tyres.RunFlat     = details.RunFlat;
      details.Rear.Tyres.FitmentType  = details.FitmentType;
      details.Rear.Tyres.RunFlat      = details.RunFlat;

      return details;
    });
  }

  hasRegistrationId ( vehicle : VehicleModel ) : boolean {
    return !!vehicle.registration_lookup_vehicle_id;
  }

  purchaseRequests () : ng.IPromise<any> {
    return this.$uibModal.open({
      controller : 'PurchaseVehicleRegistrationRequestsCtrl',
      templateUrl: PurchaseVehicleRegistrationRequestsHtml,
      resolve    : {
        prices: () => this.getRequestPrices()
      }
    }).result;
  }

  search ( vehicle : VehicleModel ) : ng.IPromise<VehicleModel> {
    if (this.$rootScope.Company.hasIntegration('vehicle-registrations')) {
      return this.$q.when()
      .then(() => this.$rootScope.Company.country('newZealand') ? this.checkaApi.search(vehicle) : this.findVehicle(vehicle))
      .catch(( err : any ) => {
        if (err.status === 402 && err.data && err.data.type === 'Q') {
          this.messages.show(this.$translate.instant('JS_SPACE.CHECKA.INSUFFICIENT_REQUESTS'), 'error');

          return this.purchaseRequests()
          .then(() => this.search(vehicle));
        }

        return this.$q.reject(err);
      });
    }

    if (this.$rootScope.Company.hasIntegration('dvla-lookup')) {
      return this.dvlaLookupApi.search(vehicle);
    }

    if (this.$rootScope.Company.hasIntegration('carfax')) {
      return this.carfaxApi.search(vehicle);
    }
  }

  viewTireDetails ( vehicle : VehicleModel ) : ng.IPromise<void> {
    return this.$uibModal.open({
      controller: 'VehicleRegistrationTireDetailsCtrl',
      resolve   : {
        tire   : () => this.getTireDetails(vehicle),
        vehicle: () => vehicle
      },
      templateUrl: VehicleRegistrationTireDetailsHtml,
      windowClass: 'x-lg-modal',
    }).result;
  }
}
